import { ComparePlansDialog } from "@/dialogs/compare-plans-dialog";
import { track } from "@/features/analytics";
import { useGetAccountQuery } from "@/shared/services";
import { openTeamDialog } from "@/shared/utils/open-team-dialog";
import Button from "@components/ui/button";
import Tooltip from "@components/ui/tooltip";
import { useAccountStoreShallow } from "@features/account-management";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import TeamsBrandingIcon from "@/assets/icons/teams-branding.svg?react";
import ProBrandingIcon from "@/assets/icons/pro-branding.svg?react";
import { IconCrown } from "@tabler/icons-react";
import { FaviconIcon } from "../icons";
import { useSidebarStore } from "../store";

const handleOpenTeam = (event: string) => {
  track(event, { surface: "sidebar header" });
  openTeamDialog("sidebar header");
};

export const SidebarHeader = () => {
  const isOpen = useSidebarStore((s) => s.isOpen);
  const { data: account } = useGetAccountQuery();
  const planType = useAccountStoreShallow((s) => s.planType);
  const isTrialEnded = useAccountStoreShallow((s) => s.isTrialEnded);
  // The store defaults planType to "trial" before the account arrives, so the
  // upsell buttons wait for the real record.
  const isPlanKnown = !!account;
  const isTeam = planType === "team";
  const isPro = isPlanKnown && planType === "pro";
  const isBasic =
    isPlanKnown &&
    !isTrialEnded &&
    (planType === "trial" || planType === "starter");
  const dispatch = useAppDispatch();

  if (!isOpen) {
    return (
      <div className="tw-flex tw-min-h-16 tw-w-full tw-flex-col tw-items-start tw-gap-2 tw-px-2 tw-pb-2 tw-pt-3">
        <div className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[10px]">
          {isTeam && account?.favicon ? (
            <img
              src={account.favicon}
              alt="favicon"
              className="tw-size-full tw-object-cover"
            />
          ) : (
            <FaviconIcon className="tw-text-white" />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tw-flex tw-min-h-16 tw-w-full tw-items-center tw-justify-between tw-p-3.5">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
          <div className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[10px]">
            {isTeam && account?.favicon ? (
              <img
                src={account.favicon}
                alt="favicon"
                className="tw-size-full tw-object-cover"
              />
            ) : (
              <FaviconIcon className="tw-text-white" />
            )}
          </div>
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-justify-center">
            <p className="tw-truncate tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-text-blue">
              Storydoc
            </p>
            {account?.title && (
              <Tooltip placement="top">
                <Tooltip.Trigger>
                  <p className="tw-max-w-44 tw-truncate tw-text-left tw-font-sans tw-text-xs tw-font-normal tw-leading-4 tw-text-blue/60">
                    {account.title}
                  </p>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Tooltip.Text className="tw-max-w-64 tw-text-center">
                    You can change the organization name in the organization
                    settings.
                  </Tooltip.Text>
                </Tooltip.Content>
              </Tooltip>
            )}
          </div>
        </div>
        {/* <button
        type="button"
        aria-label="Notifications"
        className={cn(
          "tw-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-outline-none tw-transition-colors",
          "hover:tw-bg-blue-100/50",
        )}
      >
        <IconBell className="tw-size-4 tw-text-blue" />
      </button> */}
      </div>
      {isPro && (
        <div className="tw-px-2 tw-pb-2">
          <Button
            onClick={() => handleOpenTeam("click upgrade your workspace")}
            className="tw-w-full tw-gap-1.5 tw-whitespace-nowrap tw-px-3.5 tw-leading-5 [&_svg]:tw-size-4"
            variant="secondary"
          >
            <TeamsBrandingIcon />
            Upgrade to Teams
          </Button>
        </div>
      )}
      {isBasic && (
        <div className="tw-px-2 tw-pb-2">
          <Button
            variant="secondary"
            onClick={() => {
              track("click upgrade plan", { surface: "sidebar header" });
              dispatch(
                openDialog({
                  id: ComparePlansDialog.id,
                  content: ComparePlansDialog,
                }),
              );
            }}
            className="tw-w-full tw-gap-1.5 tw-whitespace-nowrap tw-px-3.5 tw-leading-5 [&_svg]:tw-size-4"
          >
            <ProBrandingIcon />
            Upgrade
          </Button>
        </div>
      )}
      {isTrialEnded && (
        <div className="tw-px-2 tw-pb-2">
          <div className="tw-flex tw-items-center tw-justify-between tw-rounded-[10px] tw-bg-blue-100 tw-py-1 tw-pl-3 tw-pr-1 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-blue/5">
            <span className="tw-font-sans tw-text-xs tw-font-normal tw-text-blue-500">
              Your trial has ended
            </span>
            <Button
              variant="pro"
              size="sm"
              className="tw-h-7 tw-rounded-lg tw-px-2"
              onClick={() => {
                track("click select plan in trial banner", {
                  surface: "sidebar header",
                });
                dispatch(
                  openDialog({
                    id: ComparePlansDialog.id,
                    content: ComparePlansDialog,
                  }),
                );
              }}
            >
              <IconCrown className="tw-mr-1 tw-size-4 !tw-fill-none tw-text-white" />
              Select plan
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
